import { isUitkHeaderSizeValid, isUitkHeaderTagValid } from 'models/common/common-types';
import { PropertySearchListingViewModel } from 'models/hotel/search-types';
import { ResultType } from 'src/common/constants/resultType';

import { CrossSellProductType, LodgingCrossSellProductType, LodgingPwaPropertySearch, MessageType } from '__generated__/api';
import { mapMessageModel } from './search-results-adapter';

const PROPERTY_KEY_PREFIX = 'resultListing';
const MESSAGE_KEY_PREFIX = 'resultMessageListing';
const STATIC_MULTI_ITEM_CROSS_SELL_KEY_PREFIX = 'staticMultiItemCrossSell';
const STATIC_LODGING_HEADING_KEY_PREFIX = 'lodgingHeading';
const LODGING_INTENT_CARD_KEY_PREFIX = 'lodgingIntentCard';
const LODGING_CARD_KEY_PREFIX = 'lodgingCard';
const LODGING_LISTING_DIVIDER_KEY_PREFIX = 'lodgingListingDivider';
const PROPERTY_SEARCH_SOLDOUT_CARD_PREFIX = 'soldCard';
const PROPERTY_SEARCH_OVERFILTERED_CARD_PREFIX = 'overfilteredCard';
const PROPERTY_LISTING_STANDARD_MESSAGING_CARD = 'standardMessagingCard';
const PROPERTY_LISTING_EXPANDO_CARD = 'expandoCard';
const PROPERTY_DATA_STID = 'property-listing';
const SPONSORED_CONTENT_PLACEMENT_PREFIX = 'sponsoredContentPlacement';

export const mapPropertySearchListings = (query: LodgingPwaPropertySearch.Query): PropertySearchListingViewModel[] => {
  const { propertySearchListings } = query.propertySearch;

  if (!propertySearchListings) {
    return null;
  }

  return propertySearchListings
    .map((propertySearchListing, index) => {
      switch (propertySearchListing.__typename) {
        case 'Property':
          return adaptProperty(propertySearchListing as LodgingPwaPropertySearch.PropertyInlineFragment, index);
        case 'MessageResult':
          return adaptMessage(propertySearchListing as LodgingPwaPropertySearch.MessageResultInlineFragment, index);
        case 'LodgingHeading':
          return adaptLodgingHeading(propertySearchListing as LodgingPwaPropertySearch.LodgingHeadingInlineFragment, index);
        case 'StaticLodgingMultiItemCrossSell':
          return adaptMultiItemLodgingStaticCrossSell(
            propertySearchListing as LodgingPwaPropertySearch.StaticLodgingMultiItemCrossSellInlineFragment,
            index
          );
        case 'LodgingIntentCard':
          return adaptLodgingIntentCard(propertySearchListing as LodgingPwaPropertySearch.LodgingIntentCardInlineFragment, index);
        case 'LodgingCard':
          return adaptLodgingCard(propertySearchListing as LodgingPwaPropertySearch.LodgingCardInlineFragment, index);
        case 'SponsoredContentPlacement':
          return adaptSponsoredContentPlacement(
            propertySearchListing as LodgingPwaPropertySearch.SponsoredContentPlacementInlineFragment,
            index
          );
        case 'LodgingListingDivider':
          return adaptLodgingListingDivider(propertySearchListing as LodgingPwaPropertySearch.LodgingListingDividerInlineFragment, index);
        case 'PropertySearchListingsSoldOutCard':
          return adaptSoldoutCard(propertySearchListing as LodgingPwaPropertySearch.PropertySearchListingsSoldOutCardInlineFragment, index);
        case 'PropertySearchListingsOverfilteredCard':
          return adaptOverfilteredCard(
            propertySearchListing as LodgingPwaPropertySearch.PropertySearchListingsOverfilteredCardInlineFragment,
            index
          );
        case 'EGDSStandardMessagingCard':
          return adaptPropertyListingStandardMessagingCard(
            propertySearchListing as LodgingPwaPropertySearch.EgdsStandardMessagingCardInlineFragment,
            index
          );
        case 'PropertyListingsIconTextExpando':
          return adaptPropertyListingExpandoCard(
            propertySearchListing as LodgingPwaPropertySearch.PropertyListingsIconTextExpandoInlineFragment,
            index
          );
        default:
          return null;
      }
    })
    .filter(Boolean);
};

const adaptLodgingListingDivider = (listingDivider: LodgingPwaPropertySearch.LodgingListingDividerInlineFragment, index: number) => {
  const resultType = ResultType.LODGING_LISTING_DIVIDER;
  const key = `${LODGING_LISTING_DIVIDER_KEY_PREFIX}-${index}`;

  return { key, model: listingDivider, resultType };
};

const adaptSoldoutCard = (soldoutCard: LodgingPwaPropertySearch.PropertySearchListingsSoldOutCardInlineFragment, index: number) => {
  const resultType = ResultType.PROPERTY_SEARCH_SOLDOUT_CARD;
  const key = `${PROPERTY_SEARCH_SOLDOUT_CARD_PREFIX}-${index}`;

  return { key, model: soldoutCard, resultType };
};

const adaptOverfilteredCard = (
  overfilteredCard: LodgingPwaPropertySearch.PropertySearchListingsOverfilteredCardInlineFragment,
  index: number
) => {
  const resultType = ResultType.PROPERTY_SEARCH_OVERFILTERED_CARD;
  const key = `${PROPERTY_SEARCH_OVERFILTERED_CARD_PREFIX}-${index}`;

  return { key, model: overfilteredCard, resultType };
};

const adaptPropertyListingStandardMessagingCard = (
  standardMessagingCard: LodgingPwaPropertySearch.EgdsStandardMessagingCardInlineFragment,
  index: number
) => {
  const resultType = ResultType.PROPERTY_LISTING_STANDARD_MESSAGING_CARD;
  const key = `${PROPERTY_LISTING_STANDARD_MESSAGING_CARD}-${index}`;

  return { key, model: standardMessagingCard, resultType };
};

const adaptPropertyListingExpandoCard = (
  expandoCard: LodgingPwaPropertySearch.PropertyListingsIconTextExpandoInlineFragment,
  index: number
) => {
  const resultType = ResultType.PROPERTY_LISTING_EXPANDO_CARD;
  const key = `${PROPERTY_LISTING_EXPANDO_CARD}-${index}`;

  return { key, model: expandoCard, resultType };
};

const adaptLodgingCard = (lodgingCard: LodgingPwaPropertySearch.LodgingCardInlineFragment, index: number) => {
  const resultType = ResultType.LODGING_CARD;
  const key = `${LODGING_CARD_KEY_PREFIX}-${index}`;

  return { key, model: lodgingCard, resultType };
};

const adaptProperty = (property: LodgingPwaPropertySearch.PropertyInlineFragment, index: number) => {
  const id = Number.parseInt(property.id, 10);
  const resultType = ResultType.HOTEL;
  const key = id ? `${PROPERTY_KEY_PREFIX}-${id}-${index}` : `${PROPERTY_KEY_PREFIX}-${index}`;
  const dataStid = PROPERTY_DATA_STID;

  return { key, model: property, resultType, 'data-stid': dataStid };
};

const adaptMessage = (message: LodgingPwaPropertySearch.MessageResultInlineFragment, index: number) => {
  const model = mapMessageModel(message, 'MessageResult');
  const key = `${MESSAGE_KEY_PREFIX}-${index}`;
  let resultType;

  switch (message.type) {
    case MessageType.FREE_CANCELLATION:
      resultType = ResultType.FREE_CANCELLATION;
      break;
    default:
      resultType = ResultType.MESSAGING;
      break;
  }

  return { key, model, resultType };
};

const adaptLodgingIntentCard = (lodgingIntentCard: LodgingPwaPropertySearch.LodgingIntentCardInlineFragment, index: number) => {
  const resultType = ResultType.LODGING_INTENT_CARD;
  const key = `${LODGING_INTENT_CARD_KEY_PREFIX}-${index}`;

  return { key, model: lodgingIntentCard, resultType };
};

const adaptLodgingHeading = ({ size, tag, ...rest }: LodgingPwaPropertySearch.LodgingHeadingInlineFragment, index: number) => {
  if (isUitkHeaderTagValid(tag) && isUitkHeaderSizeValid(size)) {
    return {
      key: `${STATIC_LODGING_HEADING_KEY_PREFIX}-${index}`,
      resultType: ResultType.LIST_TITLE,
      model: {
        ...rest,
        size,
        tag,
      },
    };
  }

  return null;
};

const adaptMultiItemLodgingStaticCrossSell = (
  { crossSellProductType, fallbackListing, ...rest }: LodgingPwaPropertySearch.StaticLodgingMultiItemCrossSellInlineFragment,
  index: number
) => ({
  key: `${STATIC_MULTI_ITEM_CROSS_SELL_KEY_PREFIX}-${index}`,
  resultType: ResultType.STATIC_LODGING_MULTI_ITEM_CROSS_SELL,
  model: {
    ...rest,
    crossSellProductType: toCrossSellProductType(crossSellProductType),
    fallbackListingViewModel: fallbackListing ? adaptFallbackListing(fallbackListing) : null,
  },
});

const adaptFallbackListing = (fallbackListing: LodgingPwaPropertySearch.FallbackListing) => {
  switch (fallbackListing.__typename) {
    case 'MessageResult':
      return mapMessageModel(fallbackListing, 'MessageResult');
    default:
      return null;
  }
};

const toCrossSellProductType = (crossSellProductType: LodgingCrossSellProductType): CrossSellProductType | undefined => {
  switch (crossSellProductType) {
    case LodgingCrossSellProductType.CAR:
      return CrossSellProductType.CAR;
    default:
      return undefined;
  }
};

const adaptSponsoredContentPlacement = (placement: LodgingPwaPropertySearch.SponsoredContentPlacementInlineFragment, index: number) => {
  const resultType = ResultType.SPONSORED_CONTENT_PLACEMENT;
  const key = `${SPONSORED_CONTENT_PLACEMENT_PREFIX}-${index}`;

  return { key, model: placement, resultType };
};
